import { lazy, Suspense } from "react";
import ChunkErrorBoundary from "@/components/ChunkErrorBoundary";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Index from "@/pages/Index";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { WorkspaceProvider } from "@/contexts/WorkspaceContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import NotFound from "@/pages/NotFound";
import DevToolsPanel from "@/components/dev/DevToolsPanel";

// Lazy-loaded routes
const Login = lazy(() => import("@/pages/Login"));
const Signup = lazy(() => import("@/pages/Signup"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const InviteAccept = lazy(() => import("@/pages/InviteAccept"));
const DashboardLayout = lazy(() => import("@/layouts/DashboardLayout"));
const AdminLayout = lazy(() => import("@/layouts/AdminLayout"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const DashboardSettings = lazy(() => import("@/pages/DashboardSettings"));
const DashboardShorten = lazy(() => import("@/pages/DashboardShorten"));
const DashboardShortenDetail = lazy(() => import("@/pages/DashboardShortenDetail"));
const DashboardBooking = lazy(() => import("@/pages/DashboardBooking"));
const DashboardBookingAppointments = lazy(() => import("@/pages/DashboardBookingAppointments"));
const DashboardForms = lazy(() => import("@/pages/DashboardForms"));
const DashboardFormEdit = lazy(() => import("@/pages/DashboardFormEdit"));
const DashboardFormResponses = lazy(() => import("@/pages/DashboardFormResponses"));
const DashboardBilling = lazy(() => import("@/pages/DashboardBilling"));
const DashboardLinkAnalytics = lazy(() => import("@/pages/DashboardLinkAnalytics"));
const DashboardPromptPay = lazy(() => import("@/pages/DashboardPromptPay"));
const DashboardTeam = lazy(() => import("@/pages/DashboardTeam"));
const DashboardQRCodes = lazy(() => import("@/pages/DashboardQRCodes"));
const DashboardQRCodeCreate = lazy(() => import("@/pages/DashboardQRCodeCreate"));
const DashboardQRCodeDetail = lazy(() => import("@/pages/DashboardQRCodeDetail"));
const DashboardBioLinks = lazy(() => import("@/pages/DashboardBioLinks"));
const DashboardContacts = lazy(() => import("@/pages/DashboardContacts"));
const DashboardContactDetail = lazy(() => import("@/pages/DashboardContactDetail"));
const DashboardHeatTime = lazy(() => import("@/pages/DashboardHeatTime"));
const SalespageList = lazy(() => import("@/pages/SalespageList"));
const SalespageBuilder = lazy(() => import("@/pages/SalespageBuilder"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminContent = lazy(() => import("@/pages/admin/AdminContent"));
const AdminAnnouncements = lazy(() => import("@/pages/admin/AdminAnnouncements"));
const AdminBilling = lazy(() => import("@/pages/admin/AdminBilling"));
const AdminSystem = lazy(() => import("@/pages/admin/AdminSystem"));
const RedirectPage = lazy(() => import("@/pages/RedirectPage"));
const QRRedirect = lazy(() => import("@/pages/QRRedirect"));
const PublicProfile = lazy(() => import("@/pages/PublicProfile"));
const PublicBooking = lazy(() => import("@/pages/PublicBooking"));
const PublicForm = lazy(() => import("@/pages/PublicForm"));
const PublicPromptPay = lazy(() => import("@/pages/PublicPromptPay"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <WorkspaceProvider>
              <ChunkErrorBoundary>
                <Suspense fallback={null}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/invite/:token" element={<InviteAccept />} />
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <DashboardLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<Dashboard />} />
                      <Route path="bio-links" element={<DashboardBioLinks />} />
                      <Route path="contacts" element={<DashboardContacts />} />
                      <Route path="contacts/:contactId" element={<DashboardContactDetail />} />
                      <Route path="heat-time" element={<DashboardHeatTime />} />
                      <Route path="links/analytics" element={<DashboardLinkAnalytics />} />
                      <Route path="settings" element={<DashboardSettings />} />
                      <Route path="shorten" element={<DashboardShorten />} />
                      <Route path="shorten/:id" element={<DashboardShortenDetail />} />
                      <Route path="booking" element={<DashboardBooking />} />
                      <Route path="booking/appointments" element={<DashboardBookingAppointments />} />
                      <Route path="forms" element={<DashboardForms />} />
                      <Route path="forms/:id/edit" element={<DashboardFormEdit />} />
                      <Route path="forms/:id/responses" element={<DashboardFormResponses />} />
                      <Route path="billing" element={<DashboardBilling />} />
                      <Route path="promptpay" element={<DashboardPromptPay />} />
                      <Route path="team" element={<DashboardTeam />} />
                      <Route path="qr-codes" element={<DashboardQRCodes />} />
                      <Route path="qr-codes/create" element={<DashboardQRCodeCreate />} />
                      <Route path="qr-codes/:id" element={<DashboardQRCodeDetail />} />
                      <Route path="salespages" element={<SalespageList />} />
                      <Route path="salespage/new" element={<SalespageBuilder />} />
                      <Route path="salespage/:id/edit" element={<SalespageBuilder />} />
                    </Route>
                    <Route
                      path="/admin"
                      element={
                        <AdminRoute>
                          <AdminLayout />
                        </AdminRoute>
                      }
                    >
                      <Route index element={<AdminDashboard />} />
                      <Route path="users" element={<AdminUsers />} />
                      <Route path="content" element={<AdminContent />} />
                      <Route path="announcements" element={<AdminAnnouncements />} />
                      <Route path="billing" element={<AdminBilling />} />
                      <Route path="system" element={<AdminSystem />} />
                    </Route>
                    <Route path="/s/:code" element={<RedirectPage />} />
                    <Route path="/q/:code" element={<QRRedirect />} />
                    <Route path="/u/:username" element={<PublicProfile />} />
                    <Route path="/u/:username/:slug" element={<PublicProfile />} />
                    <Route path="/booking/:username" element={<PublicBooking />} />
                    <Route path="/f/:slug" element={<PublicForm />} />
                    <Route path="/pay/:username" element={<PublicPromptPay />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </ChunkErrorBoundary>
              <DevToolsPanel />
            </WorkspaceProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
